<script setup>
// Create a deck by uploading a CSV. Columns (case-insensitive): question,
// A/B/C/D (or option1–4), correct (A–D, 1–4, or exact option text), and optional
// difficulty / type / tags. Parsed + validated client-side (lib/parseCsv.js),
// previewed, then POSTed to /api/library/decks (same endpoint the manual form uses).
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import { parseCsv, mapRowsToItems } from "../../lib/parseCsv.js";

const router = useRouter();
const LETTERS = ["A", "B", "C", "D"];

const form = ref({ topic: "", slug: "", area: "", tagsText: "" });
let slugTouched = false;
const fileName = ref("");
const items = ref([]);
const rowErrors = ref([]);
const parseError = ref("");
const saving = ref(false);
const error = ref("");
const problems = ref([]);

const canCreate = computed(() => form.value.topic.trim() && items.value.length > 0 && rowErrors.value.length === 0);

function slugify(s) {
  return String(s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 64);
}
function onTopicInput() { if (!slugTouched) form.value.slug = slugify(form.value.topic); }

async function onFile(e) {
  parseError.value = ""; error.value = ""; problems.value = [];
  items.value = []; rowErrors.value = [];
  const file = e.target.files?.[0];
  if (!file) return;
  fileName.value = file.name;
  if (!form.value.topic) { form.value.topic = file.name.replace(/\.csv$/i, ""); onTopicInput(); }
  try {
    const text = await file.text();
    const { rows } = parseCsv(text);
    if (rows.length === 0) { parseError.value = "No data rows found in that CSV."; return; }
    const mapped = mapRowsToItems(rows);
    items.value = mapped.items;
    rowErrors.value = mapped.errors;
  } catch (err) {
    parseError.value = err.message || "Could not read that file.";
  }
}

async function create() {
  error.value = ""; problems.value = []; saving.value = true;
  try {
    const body = {
      topic: form.value.topic.trim(),
      slug: form.value.slug || undefined,
      area: form.value.area || undefined,
      tags: form.value.tagsText.split(",").map((t) => t.trim()).filter(Boolean),
      source: { type: "csv", name: fileName.value, importedAt: new Date().toISOString() },
      items: items.value
    };
    const r = await fetch("/api/library/decks", {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" }, body: JSON.stringify(body)
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) { if (Array.isArray(data.problems)) problems.value = data.problems; throw new Error(data.error || "We couldn't import the deck just now — please try again."); }
    router.push(`/library/${data.deck.id}`);
  } catch (e) {
    error.value = e.message;
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <div class="container-fluid p-0">
    <router-link to="/library" class="btn btn-sm btn-link px-0 mb-2"><i class="bi bi-arrow-left me-1"></i>My Library</router-link>
    <h1 class="h3 mb-1">Import a deck from CSV</h1>
    <p class="text-muted">
      Columns (case-insensitive): <code>question</code>, <code>A</code>–<code>D</code> (or <code>option1</code>–<code>4</code>),
      <code>correct</code> (a letter, 1–4, or the exact option text), and optional <code>difficulty</code> / <code>type</code> / <code>tags</code>.
    </p>

    <div v-if="error" class="alert alert-warning py-2">
      {{ error }}
      <ul v-if="problems.length" class="mb-0 mt-1 small"><li v-for="(p, i) in problems" :key="i">{{ p }}</li></ul>
    </div>

    <div class="card border-0 shadow-sm mb-3">
      <div class="card-body">
        <div class="row g-3">
          <div class="col-12 col-md-7">
            <label class="form-label small text-muted">Topic *</label>
            <input v-model="form.topic" @input="onTopicInput" type="text" class="form-control" placeholder="Deck title">
          </div>
          <div class="col-12 col-md-5">
            <label class="form-label small text-muted">Slug</label>
            <input v-model="form.slug" @input="slugTouched = true" type="text" class="form-control" placeholder="auto from topic">
          </div>
          <div class="col-12 col-md-6">
            <label class="form-label small text-muted">Area</label>
            <input v-model="form.area" type="text" class="form-control" placeholder="e.g. Driver Awareness">
          </div>
          <div class="col-12 col-md-6">
            <label class="form-label small text-muted">Tags (comma-separated)</label>
            <input v-model="form.tagsText" type="text" class="form-control" placeholder="module-1, exam">
          </div>
          <div class="col-12">
            <label class="form-label small text-muted">CSV file</label>
            <input type="file" accept=".csv,text/csv" class="form-control" @change="onFile">
          </div>
        </div>
      </div>
    </div>

    <div v-if="parseError" class="alert alert-warning py-2">{{ parseError }}</div>

    <div v-if="rowErrors.length" class="alert alert-danger py-2">
      <strong>{{ rowErrors.length }} row(s) skipped:</strong>
      <ul class="mb-0 mt-1 small">
        <li v-for="re in rowErrors" :key="re.line">Line {{ re.line }}: {{ re.errors.join("; ") }}</li>
      </ul>
    </div>

    <div v-if="items.length" class="card border-0 shadow-sm mb-3">
      <div class="card-body">
        <h2 class="h6 mb-3">Preview — {{ items.length }} question{{ items.length === 1 ? "" : "s" }}</h2>
        <div class="table-responsive" style="max-height: 360px; overflow:auto">
          <table class="table table-sm align-middle mb-0">
            <thead><tr class="text-muted small"><th>#</th><th>Question</th><th>Answer</th></tr></thead>
            <tbody>
              <tr v-for="(it, i) in items" :key="i">
                <td class="text-muted">{{ i + 1 }}</td>
                <td>
                  <div>{{ it.question }}</div>
                  <div class="small text-muted">
                    <span v-for="(o, oi) in it.options" :key="oi" :class="{ 'text-success fw-semibold': oi === it.correctIndex }">{{ LETTERS[oi] }}. {{ o }}<span v-if="oi < 3"> · </span></span>
                  </div>
                </td>
                <td><span class="badge bg-success-subtle text-success border border-success-subtle">{{ LETTERS[it.correctIndex] }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="d-flex gap-2">
      <button class="btn btn-primary" :disabled="!canCreate || saving" @click="create">
        <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
        Create deck from CSV
      </button>
      <router-link to="/library" class="btn btn-outline-secondary">Cancel</router-link>
    </div>
    <p v-if="rowErrors.length" class="text-muted small mt-2">Fix the flagged rows in your CSV and re-upload — import requires all rows valid.</p>
  </div>
</template>
